/* Khung dùng chung của App Tư vấn cơ sở: tiêu đề, chọn cơ sở / tư vấn viên, thanh khách đang mở và các bước của lượt tư vấn */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Popconfirm, Segmented, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlertTriangle, ArrowRightLeft, Check, FileText, Flag, Lock, Receipt, RotateCcw, Stethoscope, UserRound, Users } from 'lucide-react';
import Layout from '../../Layout';
import { accountIdentity } from '../../utils/accountRole';
import { consultantOf } from '../Telesales/dashboard/data';
import { Avatar, Card } from '../Telesales/dashboard/ui';
import { useLeadStore, currentStaffName } from '../Telesales/lead/store';
import {
  REASSIGN_MIN, RESULTS, STAFF_ST, STEPS, autoDispatch, VISIT_ST, WAIT_WARN_MIN, cardSummary, doctorName, durText, inExecRoom, isToday, minutes, staffNameOf, stepDone,
  type DispatchWhy, type QLine, type Result, type Screening, type Step, type Visit,
  screenFlags, screenReqs,
} from './logic';
import { loadDeclarations, toScreening, type Declaration } from './declarationApi';
import { useExamOpen, useExamSync } from './examApi';
import { useTuVan, type ViewRole } from './store';
import { apiSvcsOf, leadIdOfApi, newFromTele, teleExtras } from './tele';
import { freeRooms, roomBoard } from './reception';
import { assignVisit, setDuty, startServerVisit, useBoardRefs, useDoctors, useReception, useReceptionSync } from './receptionApi';
import { kioskDayAppointments } from '../CheckIn/kioskApi';

export { Card };
export const PRIMARY = '#3e91ab';

/** Đồng hồ làm mới thời gian chờ */
export function useNow(ms = 30_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/** Tên hiển thị: mã tư vấn viên → họ tên */
export const who = (by?: string) => staffNameOf(by) ?? consultantOf(by)?.name ?? by ?? '—';
/** Người đang thao tác */
export const useMe = () => {
  const me = useTuVan((s) => s.me);
  return { id: me, name: staffNameOf(me) ?? consultantOf(me)?.name ?? currentStaffName() };
};

/** Đang xem với vai trò bác sĩ: chỉ bước Bác sĩ khám, ẩn mọi số tiền */
/** Tài khoản đang đăng nhập; tài khoản nhân viên có sẵn vai trò ở reception_role */
const accountInfo = (): { id?: string; receptionRole?: string } => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const id = u?.info?.id ?? u?.id;
    return { id: id != null ? String(id) : undefined, receptionRole: (u?.info ?? u)?.reception_role ?? undefined };
  } catch {
    return {};
  }
};
/** Tài khoản bác sĩ, tư vấn viên bị khoá đúng vai trò; quản lý, admin vẫn được chuyển để xem */
export const lockedRole = (): ViewRole | undefined => {
  const r = accountInfo().receptionRole;
  return r === 'doctor' ? 'doctor' : r === 'consultant' ? 'consultant' : undefined;
};
export const useDoctorView = () => {
  const role = useTuVan((s) => s.role);
  return (lockedRole() ?? role) === 'doctor';
};

export interface ScreeningState { s?: Screening; flags: string[]; reqs: string[]; loading: boolean; error?: string; /** Tài khoản không có quyền đọc phiếu: tải lại cũng vậy */ forbidden?: boolean; declId?: number; edited?: boolean; source?: 'kiosk' | 'staff' }

/**
 * Khai báo ban đầu của lượt: có lịch hẹn thì đọc trên server, cờ y khoa do server tính.
 * Lượt không có lịch hẹn, khách mẫu, phiếu khai trước khi nối API thì đọc trên máy.
 */
export const useScreening = (v?: Visit): ScreeningState => {
  const byAppt = useTuVan((s) => (v?.apiApptId != null ? s.screenings[String(v.apiApptId)] : undefined));
  const local = v?.screening ?? byAppt;
  const apptId = v?.apiApptId;
  // Khách vãng lai không có lịch hẹn: đọc phiếu theo lượt
  const visitId = apptId == null ? v?.serverId : undefined;
  const key = apptId != null ? `a${apptId}` : visitId != null ? `v${visitId}` : undefined;
  const [res, setRes] = useState<{ key: string; decl?: Declaration; error?: string; forbidden?: boolean }>();
  useEffect(() => {
    if (!key) return undefined;
    let stale = false;
    loadDeclarations({ appt: apptId, visit: visitId }).then((r) => {
      if (stale) return;
      if (r.ok) setRes({ key, decl: r.data?.items[0] });
      else if (r.status === 404) setRes({ key });
      else if (r.status === 403) setRes({ key, forbidden: true, error: 'Bạn không có quyền đọc phiếu khai báo' });
      else setRes({ key, error: r.message || 'Không tải được phiếu khai báo' });
    });
    return () => { stale = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = key && res?.key === key ? res : undefined;
  // Không có quyền thì cũng không hiện bản lưu trên máy
  if (cur?.forbidden) return { flags: [], reqs: [], loading: false, error: cur.error, forbidden: true };
  if (cur?.decl) return { s: toScreening(cur.decl), flags: cur.decl.flags ?? [], reqs: cur.decl.requirements ?? [], loading: false, declId: cur.decl.id, edited: !!cur.decl.has_revisions, source: cur.decl.source };
  return { s: local, flags: screenFlags(local), reqs: screenReqs(local), loading: !!key && !cur, error: cur?.error };
};

/* ---------------- Ghi ngược về Telesales ---------------- */

const teleOf = (v: Visit) => (v.leadId && v.apptId ? { leadId: v.leadId, apptId: v.apptId } : null);
export const teleStart = (v: Visit, consultant: string, by: string) => {
  const t = teleOf(v);
  if (t) useLeadStore.getState().setApptStatus(t.leadId, t.apptId, 'arrived', by, { consultant });
};
export const teleResult = (v: Visit, r: Result) => {
  if (!v.leadId) return;
  const lead = useLeadStore.getState();
  // Lượt lấy từ server không có mã lịch trên máy: tìm theo mã lịch trên hệ thống
  const apptId = v.apptId ?? lead.byId[v.leadId]?.appts.find((a) => v.apiApptId != null && a.serverId === v.apiApptId)?.id;
  const o = { kind: RESULTS[r.kind].tele, amount: r.amount, note: r.note, by: r.by, at: r.at };
  if (apptId) lead.setOutcome(v.leadId, apptId, o);
  else lead.addTouch(v.leadId, { kind: 'appt', text: `Kết quả tư vấn: ${RESULTS[r.kind].label}${r.note ? ` · “${r.note}”` : ''}`, by: r.by });
};

/** Nhận khách: chuyển sang Đang tư vấn — vào phòng Lễ tân đã giữ, không có thì phòng tư vấn trống đầu tiên */
export async function startVisit(v: Visit, consultantId: string, by: string): Promise<boolean> {
  const { visits, update } = useTuVan.getState();
  const rooms = useReception.getState().rooms.filter((r) => r.type === 'consult').map((r) => r.name);
  // Chỉ tính lượt hôm nay (như bảng phòng của Lễ tân): lượt ngày trước quên đóng không được chiếm phòng Lễ tân đã giữ
  const now = Date.now();
  const used = new Set(visits.filter((x) => x.id !== v.id && isToday(x.checkInAt ?? x.createdAt, now)
    && (x.status === 'consulting' || (x.status === 'waiting' && x.room)) && x.branchId === v.branchId).map((x) => x.room));
  // Hết phòng trống thì vẫn nhận khách, chưa xếp phòng
  const room = v.room && !used.has(v.room) ? v.room : rooms.find((r) => !used.has(r));
  const res = await startServerVisit(v, consultantId, room);
  if (!res.ok || !res.visit) { message.error(res.msg); return false; }
  const name = who(consultantId);
  update(res.visit.id, () => ({}), { text: `${name} nhận khách${res.visit.room ? ` · ${res.visit.room}` : ''}`, by });
  teleStart(res.visit, name, by);
  return true;
}

/** Nhắc khai báo y tế trong danh sách: chỉ báo chưa khai và có lưu ý, không hiện tên bệnh */
export const DeclBadge = ({ v }: { v: Visit }) => {
  if (!v.serverId) return null;
  return (
    <>
      {!v.hasDecl && <Tag className="!m-0 !ml-1 !px-1 !text-[11px] !leading-4">Chưa khai</Tag>}
      {v.hasAlert && <Tooltip title="Phiếu khai báo có lưu ý y tế, xem ở Phiếu tư vấn hoặc Bác sĩ khám"><AlertTriangle size={13} aria-label="Có lưu ý y tế" className="ml-1 inline text-red-500" /></Tooltip>}
    </>
  );
};

const DISPATCH_TEXT: Record<DispatchWhy, (to: string, from: string) => string> = {
  new: (to) => `Chia tự động cho ${to}`,
  late: (to, from) => `Chia lại cho ${to} · ${from} chưa nhận khách sau ${REASSIGN_MIN} phút`,
  away: (to, from) => `Chia lại cho ${to} · ${from} đang tạm nghỉ / ngoại tuyến`,
};

/* ---------------- Khung trang ---------------- */

/**
 * Đồng bộ lượt tư vấn trong ngày: dữ liệu mẫu, lịch Telesales đã đến, lịch API đã check-in (tải lại mỗi phút)
 * và chia khách tự động — chạy ở App Tư vấn và màn Lễ tân để hai bên thấy cùng danh sách khách.
 */
export function useVisitSync() {
  useReceptionSync();
  const now = useNow(60_000);
  const { branchId, staffSt, ensureSeed, add } = useTuVan();
  const byId = useLeadStore((s) => s.byId);
  const dateKey = dayjs(now).format('YYYY-MM-DD');

  useEffect(() => {
    ensureSeed(Date.now());
    const fresh = newFromTele(byId, useTuVan.getState().visits, Date.now());
    if (fresh.length) add(fresh);
  }, [byId, dateKey, ensureSeed, add]);

  // Kế toán, POS đổi trạng thái cọc: cập nhật ngay lượt đang mở để kế hoạch thanh toán tính đúng phần còn lại
  // Chỉ tính lại lượt của khách vừa đổi hồ sơ: store Telesales đổi liên tục (lịch sử chạm, tin nhắn), không duyệt cả ngày mỗi lần
  const prevById = useRef<typeof byId | null>(null);
  const checked = useRef(new Set<string>());
  useEffect(() => {
    const st = useTuVan.getState();
    const before = prevById.current;
    prevById.current = byId;
    st.visits.forEach((v) => {
      const lead = v.leadId ? byId[v.leadId] : undefined;
      if (!lead || (before?.[v.leadId!] === lead && checked.current.has(v.id))) return;
      checked.current.add(v.id);
      const { onlineDeps } = teleExtras(lead, v.mang, { apiApptId: v.apiApptId, apptId: v.apptId });
      if (JSON.stringify(onlineDeps) !== JSON.stringify(v.onlineDeps)) st.update(v.id, () => ({ onlineDeps }));
    });
  }, [byId]);

  // Lượt do server tạo lúc check-in. Lịch hẹn chỉ để lấy thêm dịch vụ, nhu cầu và báo giá Telesales; tải lại mỗi phút
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      const res = await kioskDayAppointments(dayjs().format('YYYY-MM-DD'));
      if (!alive || !res.status) return;
      const st = useTuVan.getState();
      const leads = useLeadStore.getState().byId;
      // Lượt tạo trước khi lưu dịch vụ của lịch hẹn: bổ sung để phiếu báo giá lấy đúng dịch vụ khách đặt
      res.data.forEach((a: any) => {
        const v = st.visits.find((x) => x.apiApptId === a?.id && !x.apptSvcs);
        if (v) st.update(v.id, () => ({ apptSvcs: apiSvcsOf(a) }));
        // Lượt tạo trước khi nối hồ sơ Telesales: bổ sung nhu cầu, báo giá online
        const leadId = leadIdOfApi(a, leads);
        const w = st.visits.find((x) => x.apiApptId === a?.id && !x.leadId);
        if (w && leadId) {
          const { mang, ...extra } = teleExtras(leads[leadId], w.mang);
          st.update(w.id, () => ({ leadId, ...extra, ...(mang ? { mang } : {}) }));
        }
        // Telesales sửa / gửi lại báo giá, hoặc lượt tạo trước khi có ưu đãi đi kèm: chụp lại báo giá online theo hồ sơ
        const o = st.visits.find((x) => x.apiApptId === a?.id && x.leadId && leads[x.leadId]);
        if (o) {
          const { online, onlineDeps } = teleExtras(leads[o.leadId!], o.mang, { apiApptId: a.id });
          if (JSON.stringify(online) !== JSON.stringify(o.online) || JSON.stringify(onlineDeps) !== JSON.stringify(o.onlineDeps)) st.update(o.id, () => ({ online, onlineDeps }));
        }
        const u = st.visits.find((x) => x.apiApptId === a?.id && !x.pageStaff && a?.staff);
        if (u) st.update(u.id, () => ({ pageStaff: a.staff.fullname || a.staff.name, apptAt: a.scheduled_at ? dayjs(String(a.scheduled_at).replace(' ', 'T')).valueOf() : undefined }));
      });
    };
    pull();
    const t = setInterval(pull, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [dateKey, add]);

  // Chia khách tự động: khách mới check-in, người được chia nghỉ / quá giờ chưa nhận → chia lại
  const autoOn = useTuVan((s) => s.autoAssign);
  const refs = useBoardRefs();
  const nVisits = useTuVan((s) => s.visits.length);
  useEffect(() => {
    if (!autoOn) return undefined;
    let running = false;
    const run = async () => {
      if (running || !refs.consultants.length) return;
      running = true;
      const st = useTuVan.getState();
      const picks = autoDispatch(st.visits, st.branchId, st.staffSt, Date.now(), undefined, refs.consultants);
      for (let i = 0; i < picks.length; i += 1) {
        const d = picks[i];
        // Giữ luôn phòng tư vấn trống (đọc lại store sau mỗi lượt chia để không trùng phòng)
        const cur = useTuVan.getState();
        const cv = cur.visits.find((x) => x.id === d.id);
        const room = cv?.room ?? freeRooms(roomBoard(cur.visits, cur.branchId, Date.now(), refs), 'consult')[0];
        // eslint-disable-next-line no-await-in-loop
        const res = cv ? await assignVisit(cv, d.to, room) : undefined;
        if (res?.ok) {
          useTuVan.getState().update(d.id, () => ({}), {
            text: `${DISPATCH_TEXT[d.why](who(d.to), d.from ? who(d.from) : '')}${room ? ` · giữ ${room}` : ''}`, by: 'Chia tự động',
          });
        }
      }
      running = false;
    };
    run();
    const t = setInterval(run, 15_000);
    return () => clearInterval(t);
  }, [autoOn, branchId, nVisits, staffSt, refs]);
}

let branchDefaultedFor: string | undefined;

export function Shell({ title, sub, children, extra }: { title: string; sub: string; children: React.ReactNode; extra?: React.ReactNode }) {
  useVisitSync();
  useExamSync();
  const { branchId, me, staffSt, set, reseed, role: viewRole, meDoctor } = useTuVan();
  const locked = lockedRole();
  const role = locked ?? viewRole;
  const cons = useBoardRefs().consultants;
  const doctors = useDoctors();
  const branches = useReception((s) => s.branches);
  // Tư vấn viên đang có khách không được nghỉ (PA, P10)
  const hasGuest = useTuVan((s) => s.visits.some((v) => v.consultant === s.me && (v.status === 'waiting' || v.status === 'consulting') && isToday(v.checkInAt ?? v.createdAt, Date.now())));
  const doctor = role === 'doctor';

  // Tài khoản đăng nhập là tư vấn viên thì khoá đúng người; tài khoản quản lý chọn người để xem và thao tác thay
  const account = useMemo(() => {
    return accountInfo().id;
  }, []);
  const mine = cons.some((c) => c.id === account) ? account : undefined;
  useEffect(() => {
    if (!cons.length) return;
    if (mine) { if (me !== mine) set({ me: mine }); } else if (!me || !cons.some((c) => c.id === me)) set({ me: cons[0].id });
  }, [mine, me, cons, set]);
  // Tài khoản bác sĩ thì khoá đúng người
  const mineDoctor = locked === 'doctor' && doctors.some((d) => d.id === account) ? account : undefined;
  useEffect(() => {
    if (mineDoctor) { if (meDoctor !== mineDoctor) set({ meDoctor: mineDoctor }); } else if (!meDoctor || !doctors.some((d) => d.id === meDoctor)) set({ meDoctor: doctors[0].id });
  }, [mineDoctor, meDoctor, doctors, set]);
  // Bác sĩ, tư vấn viên mở app lần đầu trong phiên: về cơ sở của tài khoản, sau đó vẫn đổi được
  useEffect(() => {
    const a = accountIdentity();
    if (!locked || !a.id || !a.branchId || branchDefaultedFor === a.id || !branches.length) return;
    branchDefaultedFor = a.id;
    if (branches.some((b) => String(b.id) === a.branchId) && a.branchId !== branchId) set({ branchId: a.branchId });
  }, [locked, branches, branchId, set]);
  const st = me ? staffSt[me] ?? 'ready' : 'ready';

  return (
    <Layout>
      <Helmet><title>{title} | Khothi</title></Helmet>
      <div className="mx-auto w-full max-w-[1800px] space-y-3 pb-28 sm:space-y-4 sm:pb-24">
        <div className="flex flex-wrap items-end justify-between gap-2 sm:gap-3">
          <div className="min-w-0">
            <h1 className="flex flex-wrap items-center gap-2 text-lg font-bold text-gray-800 sm:text-xl dark:text-gray-100">
              {title}
              <Tooltip title="Hàng chờ, phòng và nhân sự lấy từ hệ thống. Phiếu CSKH và các bước từ Phiếu tư vấn trở đi còn lưu trên máy này, chờ API">
                <Tag color="orange" className="!m-0 cursor-help !text-[11px] !font-semibold">Một phần lưu trên máy</Tag>
              </Tooltip>
            </h1>
            <p className="hidden text-sm text-gray-500 sm:block dark:text-gray-400">{sub}</p>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
            {extra}
            {branches.length > 1 && (
              <Select value={branchId} onChange={(b) => set({ branchId: b })} className="min-w-[160px] flex-1 lg:flex-none"
                options={branches.map((b) => ({ value: String(b.id), label: b.name }))} />
            )}
            {!locked && (
              <Tooltip title="Xem với vai trò khác: bác sĩ chỉ thấy bước Bác sĩ khám và không thấy số tiền">
                <Segmented value={role} onChange={(x) => set({ role: x as typeof role })}
                  options={[{ value: 'consultant', label: 'Tư vấn viên' }, { value: 'doctor', label: 'Bác sĩ' }]} />
              </Tooltip>
            )}
            {doctor ? (
              <Select value={meDoctor} disabled={!!mineDoctor} onChange={(id) => set({ meDoctor: id })} className="min-w-0 flex-1 sm:min-w-[200px] lg:flex-none"
                options={doctors.map((d) => ({ value: d.id, label: <span className="inline-flex items-center gap-1.5"><Stethoscope size={14} className="text-gray-400" />{d.name}</span> }))} />
            ) : (
              <>
                <Select value={me} disabled={!!mine} onChange={(id) => set({ me: id })} className="min-w-0 flex-1 sm:min-w-[190px] lg:flex-none"
                  optionLabelProp="label"
                  options={cons.map((c) => ({
                    value: c.id,
                    label: <span className="inline-flex items-center gap-1.5"><UserRound size={14} className="text-gray-400" />{c.name}</span>,
                  }))} />
                <Select value={st} onChange={(x) => { if (me) setDuty(me, x).then((r) => { if (!r.ok) message.error(r.msg); }); }} className="w-[132px]"
                  options={Object.entries(STAFF_ST).map(([k, s]) => ({
                    value: k,
                    disabled: k !== 'ready' && hasGuest,
                    title: k !== 'ready' && hasGuest ? 'Đang có khách, chưa đổi sang nghỉ được' : undefined,
                    label: <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: s.dot }} />{s.label}</span>,
                  }))} />
              </>
            )}
            <Popconfirm title="Làm lại dữ liệu mẫu hôm nay?" description="Xoá mọi lượt tư vấn trên máy này và tạo lại dữ liệu mẫu." okText="Làm lại" cancelText="Huỷ" onConfirm={() => reseed(Date.now())}>
              <Tooltip title="Làm lại dữ liệu mẫu"><Button icon={<RotateCcw size={15} />} className="!hidden sm:!inline-flex" /></Tooltip>
            </Popconfirm>
          </div>
        </div>
        {doctor && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-[13px] text-cyan-900 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-100">
            <Stethoscope size={16} className="shrink-0" />
            <span className="min-w-0 flex-1">{locked
              ? <>Tài khoản <b>Bác sĩ</b> chỉ mở được bước Bác sĩ khám, ẩn mọi số tiền.</>
              : <>Đang xem với vai trò <b>Bác sĩ</b> — chỉ mở được bước Bác sĩ khám, ẩn mọi số tiền. Muốn sang các bước khác thì chuyển về Tư vấn viên.</>}</span>
            {!locked && <Button size="small" type="primary" onClick={() => set({ role: 'consultant' })}>Chuyển về Tư vấn viên</Button>}
          </div>
        )}
        {children}
      </div>
      <MobileNav />
    </Layout>
  );
}

/* ---------------- Thanh điều hướng dưới đáy (điện thoại) ---------------- */

const NAV = [
  { url: '/app-tuvan', label: 'Khách chờ', icon: Users },
  { url: '/phieu-tu-van', label: 'Phiếu', icon: FileText },
  { url: '/bac-si-kham', label: 'Khám', icon: Stethoscope },
  { url: '/phieu-bao-gia', label: 'Báo giá', icon: Receipt },
  { url: '/ket-qua-tu-van', label: 'Kết quả', icon: Flag },
  { url: '/chuyen-thuc-hien', label: 'Thực hiện', icon: ArrowRightLeft },
];

/** Tư vấn viên dùng điện thoại là chính: các màn hay dùng luôn trong tầm ngón cái; số đỏ = khách đang được chia cho mình */
function MobileNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const now = useNow();
  const me = useMe();
  const doctor = useDoctorView();
  const { visits, cur } = useTuVan();
  const mine = visits.filter((v) => v.status === 'waiting' && v.consultant === me.id && isToday(v.checkInAt, now)).length;
  if (doctor) return null;
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 border-t border-gray-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur sm:hidden dark:border-gray-700 dark:bg-gray-900/95">
      {NAV.map(({ url, label, icon: Icon }) => {
        const active = pathname === url;
        return (
          <button key={url} type="button" onClick={() => navigate(url === '/app-tuvan' || !cur ? url : `${url}?v=${cur}`)}
            className={`relative flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium ${active ? 'text-[#3e91ab]' : 'text-gray-500 dark:text-gray-400'}`}>
            <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
            {label}
            {url === '/app-tuvan' && mine > 0 && (
              <span className="absolute right-[22%] top-1 min-w-[16px] rounded-full bg-red-500 px-1 text-[10px] font-bold leading-4 text-white">{mine}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

/* ---------------- Khách đang mở + các bước ---------------- */

/** Lượt đang mở: ?v=id, không có thì lượt mở gần nhất */
export function useVisit() {
  const [sp, setSp] = useSearchParams();
  const { visits, cur, set } = useTuVan();
  const id = sp.get('v') ?? cur;
  const v = visits.find((x) => x.id === id);
  useEffect(() => { if (v && v.id !== cur) set({ cur: v.id }); }, [v, cur, set]);
  useExamOpen(v);
  return { v, pick: (x: string) => setSp({ v: x }) };
}

export const StatusTag = ({ v }: { v: Visit }) => {
  const s = v.result ? { label: RESULTS[v.result.kind].short, color: RESULTS[v.result.kind].closed ? 'green' : 'orange' } : VISIT_ST[v.status];
  return <Tag color={s.color} className="!m-0">{s.label}</Tag>;
};
export const SourceTag = ({ v }: { v: Visit }) => {
  const color = { Telesales: 'cyan', 'Đặt lịch': 'geekblue', 'Vãng lai': 'default', CSKH: 'purple', 'Khách cũ': 'blue' }[v.source];
  return <Tag color={color} className="!m-0">{v.source}</Tag>;
};
export const WaitText = ({ v, now }: { v: Visit; now: number }) => {
  if (!v.checkInAt) return <span className="text-gray-400">—</span>;
  const end = v.startedAt ?? (v.status === 'done' ? v.result?.at : undefined) ?? now;
  const late = !v.startedAt && minutes(now - v.checkInAt) >= WAIT_WARN_MIN;
  return <span className={late ? 'font-semibold text-red-500' : 'text-gray-600 dark:text-gray-300'}>{durText(end - v.checkInAt)}</span>;
};

export function FlowBar({ v, step }: { v?: Visit; step: Step }) {
  const navigate = useNavigate();
  const now = useNow();
  const { visits, branchId } = useTuVan();
  const doctor = useDoctorView();
  const { pick } = useVisit();
  const list = useMemo(() => visits
    .filter((x) => x.id === v?.id || (x.branchId === branchId && x.status !== 'referral' && x.status !== 'cancelled' && isToday(x.checkInAt ?? x.createdAt, now)))
    .sort((a, b) => ['consulting', 'waiting', 'done'].indexOf(a.status) - ['consulting', 'waiting', 'done'].indexOf(b.status) || (b.checkInAt ?? 0) - (a.checkInAt ?? 0)),
  [visits, branchId, now, v?.id]);
  // Bác sĩ vẫn thấy đủ các bước để biết khách đang ở đâu; bước có số tiền khoá lại, bấm vào sẽ hướng dẫn chuyển vai trò
  const locked = (s: Step) => doctor && s !== 'exam';

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Select
            showSearch value={v?.id} onChange={pick} placeholder="Chọn khách đang tư vấn" className="w-full sm:w-[300px]"
            optionFilterProp="search"
            options={list.map((x) => ({
              value: x.id,
              search: `${x.cust.name} ${x.cust.code} ${x.code}`,
              label: <span className="flex items-center justify-between gap-2"><span className="truncate">{x.cust.name} · {x.cust.code}</span><span className="text-xs text-gray-400">{VISIT_ST[x.status].label}</span></span>,
            }))}
          />
          {v && (
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-gray-600 dark:text-gray-300">
              <StatusTag v={v} />
              <span className="line-clamp-2 sm:line-clamp-none"><b className="text-gray-800 dark:text-gray-100">{v.mang}</b> · {v.need}</span>
              <SourceTag v={v} />
              {v.consultant && (
                <span className="inline-flex items-center gap-1.5">
                  <Avatar name={who(v.consultant)} color={consultantOf(v.consultant)?.color} size={20} />{who(v.consultant)}{v.room && <span className="text-gray-400">· {v.room}</span>}
                </span>
              )}
              {v.checkInAt && <span className="text-gray-500">Check-in {dayjs(v.checkInAt).format('HH:mm')} · chờ <WaitText v={v} now={now} /></span>}
            </div>
          )}
        </div>
        {v && (
          <div className="-mx-1 flex max-w-full gap-1.5 overflow-x-auto border-t border-gray-100 px-1 pt-3 [scrollbar-width:none] dark:border-gray-700">
            {STEPS.map((s, i) => {
              const done = stepDone(v, s.key);
              const active = s.key === step;
              const lock = locked(s.key);
              return (
                <Tooltip key={s.key} title={lock ? (lockedRole() ? 'Bước có số tiền — tài khoản bác sĩ không xem.' : 'Bước có số tiền — bác sĩ không xem. Chuyển về vai trò Tư vấn viên để mở.') : undefined}>
                  <button type="button" onClick={() => navigate(`${s.url}?v=${v.id}`)}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium transition ${active
                      ? 'border-[#3e91ab] bg-[#3e91ab] text-white'
                      : lock ? 'border-dashed border-gray-200 text-gray-400 hover:text-gray-500 dark:border-gray-600 dark:text-gray-500'
                        : 'border-gray-200 text-gray-600 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600 dark:text-gray-300'}`}>
                    <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${active ? 'bg-white/25' : done ? 'bg-green-500 text-white' : 'bg-gray-100 dark:bg-gray-700'}`}>
                      {lock && !done ? <Lock size={9} strokeWidth={2.6} /> : done ? <Check size={11} strokeWidth={3} /> : i + 1}
                    </span>
                    {s.label}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Vai trò bác sĩ mở màn có số tiền (báo giá, thanh toán, kết quả, hàng chờ): báo lý do, dẫn về Bác sĩ khám
 * hoặc chuyển về vai trò Tư vấn viên — không tự đẩy trang đi để người dùng biết vì sao không vào được
 */
export function DoctorGate({ v, children }: { v?: Visit; children: React.ReactNode }) {
  const navigate = useNavigate();
  const doctor = useDoctorView();
  const set = useTuVan((s) => s.set);
  if (!doctor) return <>{children}</>;
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <Lock size={32} className="text-gray-300" />
        <div className="text-[15px] font-semibold text-gray-700 dark:text-gray-200">Bác sĩ chỉ dùng bước Bác sĩ khám</div>
        <p className="max-w-md text-sm text-gray-500">Màn này có số tiền (báo giá, thanh toán, kết quả) nên ẩn với tài khoản bác sĩ.{!lockedRole() && ' Đang thử luồng tư vấn thì chuyển về vai trò Tư vấn viên.'}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button type="primary" icon={<Stethoscope size={14} />} onClick={() => navigate(v ? `/bac-si-kham?v=${v.id}` : '/bac-si-kham')}>
            {v ? `Mở Bác sĩ khám · ${v.cust.name}` : 'Mở Bác sĩ khám'}
          </Button>
          {!lockedRole() && <Button icon={<UserRound size={14} />} onClick={() => set({ role: 'consultant' })}>Chuyển về Tư vấn viên</Button>}
        </div>
      </div>
    </Card>
  );
}

/** Tên bác sĩ được mời / đã khám của lượt */
export const examDoctor = (v: Visit) => doctorName(v.quote?.exam.doctor ?? v.invite?.doctor);

/** Chưa chọn khách */
export function NoVisit() {
  const navigate = useNavigate();
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <UserRound size={36} className="text-gray-300" />
        <div className="text-[15px] font-semibold text-gray-700 dark:text-gray-200">Chưa chọn khách</div>
        <p className="max-w-md text-sm text-gray-500">Chọn khách ở ô phía trên, hoặc vào Khách đang chờ để nhận khách mới.</p>
        <Button type="primary" onClick={() => navigate('/app-tuvan')}>Mở Khách đang chờ</Button>
      </div>
    </Card>
  );
}

/** Khách chưa được nhận: cho nhận ngay tại chỗ */
export function NotStarted({ v }: { v: Visit }) {
  const me = useMe();
  if (v.status !== 'waiting' && v.status !== 'referral') return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
      <span>{v.status === 'referral' ? 'Khách chưa đến cơ sở — check-in ở Khách đang chờ trước khi tư vấn.' : inExecRoom(v) ? `Khách đang làm dịch vụ ở ${v.exec!.room}, nhận tư vấn sau khi Lễ tân trả phòng.` : 'Khách đang chờ, chưa có ai nhận tư vấn.'}</span>
      {v.status === 'waiting' && !inExecRoom(v) && me.id && (
        <Button size="small" type="primary" onClick={() => startVisit(v, v.consultant ?? me.id!, me.name)}>
          Nhận khách{v.consultant && v.consultant !== me.id ? ` (cho ${who(v.consultant)})` : ''}
        </Button>
      )}
    </div>
  );
}

/**
 * Bảng trên máy tính, thẻ trên điện thoại (tư vấn viên dùng điện thoại là chính): mỗi dòng một thẻ,
 * cột đầu làm tiêu đề, các cột có tiêu đề thành cặp nhãn – giá trị, cột không tiêu đề (thao tác) xuống cuối thẻ.
 */
export function RTable<T extends object>({ columns, dataSource, rowKey, empty, scrollX, className = '' }: {
  columns: ColumnType<T>[]; dataSource: T[]; rowKey: keyof T | ((r: T) => React.Key); empty: React.ReactNode; scrollX?: number; className?: string;
}) {
  const keyOf = (r: T) => (typeof rowKey === 'function' ? rowKey(r) : (r[rowKey] as unknown as React.Key));
  const cell = (c: ColumnType<T>, r: T, i: number): React.ReactNode => {
    const val = typeof c.dataIndex === 'string' ? (r as Record<string, unknown>)[c.dataIndex] : undefined;
    return c.render ? (c.render(val, r, i) as React.ReactNode) : (val as React.ReactNode);
  };
  const [head, ...rest] = columns;
  const fields = rest.filter((c) => c.title);
  const acts = rest.filter((c) => !c.title);
  return (
    <div className={className}>
      <div className="hidden sm:block">
        <Table<T> rowKey={keyOf} size="small" columns={columns} dataSource={dataSource} pagination={false} scroll={scrollX ? { x: scrollX } : undefined}
          locale={{ emptyText: <div className="py-4 text-[13px] text-gray-400">{empty}</div> }} />
      </div>
      <div className="space-y-2 sm:hidden">
        {dataSource.map((r, i) => (
          <div key={keyOf(r)} className="rounded-lg border border-gray-200 p-2.5 text-[13px] dark:border-gray-700">
            {head && <div className="font-semibold text-gray-800 dark:text-gray-100">{cell(head, r, i)}</div>}
            {fields.length > 0 && (
              <dl className="mt-1.5 grid grid-cols-[96px_1fr] gap-x-2 gap-y-1">
                {fields.map((c, k) => (
                  <React.Fragment key={k}>
                    <dt className="text-xs text-gray-500">{c.title as React.ReactNode}</dt>
                    <dd className="min-w-0 text-gray-800 dark:text-gray-100">{cell(c, r, i)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
            {acts.map((c, k) => <div key={k} className="mt-2 flex justify-end">{cell(c, r, i)}</div>)}
          </div>
        ))}
        {!dataSource.length && <div className="py-4 text-center text-[13px] text-gray-400">{empty}</div>}
      </div>
    </div>
  );
}

/** Nhãn – giá trị dạng hàng */
export const Row = ({ label, children }: { label: React.ReactNode; children: React.ReactNode }) => (
  <div className="grid grid-cols-[120px_1fr] items-center gap-2 sm:grid-cols-[140px_1fr]">
    <span className="text-[13px] text-gray-500 dark:text-gray-400">{label}</span>
    <div className="min-w-0 text-[13px] text-gray-800 dark:text-gray-100">{children}</div>
  </div>
);

export const Hint = ({ children, tone = 'gray' }: { children: React.ReactNode; tone?: 'gray' | 'red' | 'amber' }) => (
  <p className={`text-xs ${tone === 'red' ? 'text-red-500' : tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>{children}</p>
);

/** Tên dòng phiếu: dịch vụ lẻ (tag chủ lực / tặng kèm) hoặc thẻ liệu trình (mã gói, số buổi / mệnh giá, hạn dùng — không có giá, bác sĩ xem được) */
export function LineName({ l, noMoney }: { l: QLine; noMoney?: boolean }) {
  return (
    <span className="leading-snug">
      <span className="font-medium text-gray-800 dark:text-gray-100">{l.name}</span>
      {l.gift && <Tag className="!ml-1.5 !text-[10px]">Tặng kèm</Tag>}
      {l.core && <Tooltip title="Dịch vụ chủ lực: khoá giảm %"><Tag className="!ml-1.5 !text-[10px]">Chủ lực</Tag></Tooltip>}
      {l.card && <Tag color="purple" className="!ml-1.5 !text-[10px]">Thẻ liệu trình · {l.card.code}</Tag>}
      {l.card && <span className="mt-0.5 block text-xs font-normal text-gray-500">{cardSummary(l.card, !noMoney)}</span>}
    </span>
  );
}

